import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Target, Zap, Trophy, ChevronDown, ChevronUp, Shield } from "lucide-react";

export function FoursomeSection({ round, basePlayers, expandedFoursome, setExpandedFoursome, isVuelta1 }) {
  return (
    <>
      <h3 className="text-sm font-bold text-[#0F172A] mb-3 flex items-center gap-2">
        <Target className="w-4 h-4 text-[#1B4D3E]" />
        Enfrentamientos Foursome
      </h3>

      <div className="space-y-3 mb-6">
        {round?.matchups?.map((matchup, index) => {
          const secPlayers = matchup.secondary_pair.map(id => round.players.find(p => p.player_id === id));
          const isExpanded = expandedFoursome === index;

          return (
            <Card key={index} className={`card-default overflow-hidden ${matchup.is_doubled ? 'ring-2 ring-[#D4A373]' : ''}`}>
              <CardContent className="p-0">
                <button
                  className="w-full p-3 text-left"
                  onClick={() => setExpandedFoursome(isExpanded ? null : index)}
                  data-testid={`foursome-toggle-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      {matchup.is_doubled && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#D4A373] mb-1">
                          <Zap className="w-3 h-3" /> DOBLE
                        </span>
                      )}
                      {matchup.grandslam?.active && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-yellow-700 mb-1 ml-2" data-testid={`gs-badge-${index}`}>
                          <Trophy className="w-3 h-3" /> GS {matchup.grandslam.bonus > 0 ? '+' : ''}{matchup.grandslam.bonus}
                        </span>
                      )}
                      <p className="text-xs text-[#64748B]">E{index + 1}</p>
                      <p className="text-sm font-semibold text-[#0F172A] truncate">
                        {basePlayers.map(p => p.name.split(' ')[0]).join('+')} vs {secPlayers.map(p => p?.name?.split(' ')[0]).join('+')}
                      </p>
                      <div className="flex gap-3 mt-1 text-[10px] text-[#64748B]">
                        <span>V1: <span className={`font-bold ${(matchup.vuelta1_points || 0) > 0 ? 'text-green-600' : (matchup.vuelta1_points || 0) < 0 ? 'text-red-600' : ''}`}>
                          {(matchup.vuelta1_points || 0) > 0 ? '+' : ''}{matchup.vuelta1_points || 0}
                        </span></span>
                        <span>V2: <span className={`font-bold ${(matchup.vuelta2_points || 0) > 0 ? 'text-green-600' : (matchup.vuelta2_points || 0) < 0 ? 'text-red-600' : ''}`}>
                          {(matchup.vuelta2_points || 0) > 0 ? '+' : ''}{matchup.vuelta2_points || 0}
                        </span></span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`px-3 py-1.5 rounded-lg ${matchup.total_points > 0 ? 'bg-green-50' : matchup.total_points < 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                        <p className={`text-xl font-bold ${matchup.total_points > 0 ? 'text-green-600' : matchup.total_points < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {matchup.total_points > 0 ? '+' : ''}{matchup.total_points}
                        </p>
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-[#64748B]" /> : <ChevronDown className="w-4 h-4 text-[#64748B]" />}
                    </div>
                  </div>
                </button>

                {isExpanded && Object.keys(matchup.hole_results || {}).length > 0 && (
                  <div className="px-3 pb-3 border-t border-gray-100">
                    <p className="text-[10px] text-[#64748B] mt-2 mb-1">Ventaja: {matchup.handicap_advantage} golpes a {matchup.advantage_to === 'base' ? 'Base' : matchup.advantage_to === 'secondary' ? 'Sec.' : 'Ninguno'}
                      {matchup.handicap_advantage > 18 && ` (doble en hcp 1-${matchup.handicap_advantage - 18})`}
                    </p>
                    
                    {matchup.units_advantage_total > 0 && (
                      <div className="bg-amber-50 rounded-lg p-2 mb-2 border border-amber-200">
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-amber-800 font-semibold">
                            Uds. Ventaja: {matchup.units_advantage_total} a {matchup.units_advantage_to === 'base' ? 'Base' : 'Sec.'}
                          </span>
                          <span className="text-amber-600">
                            Usadas: {matchup.units_advantage_used || 0} | Quedan: {matchup.units_advantage_remaining ?? matchup.units_advantage_total}
                          </span>
                        </div>
                        {matchup.units_advantage_adjustment !== 0 && matchup.units_advantage_adjustment != null && (
                          <p className="text-[10px] text-amber-700 mt-1">
                            Ajuste: {matchup.units_advantage_adjustment > 0 ? '+' : ''}{matchup.units_advantage_adjustment} pts (total raw: {matchup.total_points_raw ?? matchup.total_points})
                          </p>
                        )}
                      </div>
                    )}
                    {[1, 2].map(vuelta => {
                      const vHoles = Object.entries(matchup.hole_results).filter(([hole]) =>
                        vuelta === 1 ? isVuelta1(parseInt(hole)) : !isVuelta1(parseInt(hole))
                      ).sort(([a], [b]) => parseInt(a) - parseInt(b));
                      if (vHoles.length === 0) return null;
                      return (
                        <div key={vuelta} className="mt-2">
                          <p className="text-[10px] font-bold text-[#64748B] mb-1">
                            {vuelta === 1 ? '1ra' : '2da'} Vuelta
                            {vuelta === 2 && matchup.is_doubled && <span className="ml-1 bg-amber-100 text-amber-700 px-1 rounded">x2</span>}
                          </p>
                          <div className="overflow-x-auto -mx-1">
                            <table className="w-full text-[11px]">
                              <thead>
                                <tr className="text-[#64748B]">
                                  <th className="text-left py-1 px-1 w-14">Hoyo</th>
                                  {vHoles.map(([hole]) => <th key={hole} className="text-center py-1 px-0.5 min-w-[30px]">{hole}</th>)}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="py-1 px-1 font-medium text-[#1B4D3E]">B.Baja</td>
                                  {vHoles.map(([hole, r]) => (
                                    <td key={hole} className={`text-center py-1 px-0.5 ${r.low_ball?.points > 0 ? 'text-green-600 font-bold' : r.low_ball?.points < 0 ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                                      {r.low_ball?.base}-{r.low_ball?.sec}
                                    </td>
                                  ))}
                                </tr>
                                <tr className="bg-gray-50/50">
                                  <td className="py-1 px-1 font-medium text-[#D4A373]">B.Alta</td>
                                  {vHoles.map(([hole, r]) => (
                                    <td key={hole} className={`text-center py-1 px-0.5 ${r.high_ball?.points > 0 ? 'text-green-600 font-bold' : r.high_ball?.points < 0 ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                                      {r.high_ball?.base}-{r.high_ball?.sec}
                                    </td>
                                  ))}
                                </tr>
                                <tr>
                                  <td className="py-1 px-1 font-medium text-[#92400e]">Uds</td>
                                  {vHoles.map(([hole, r]) => {
                                    const u = r.unit_points || 0;
                                    return <td key={hole} className={`text-center py-1 px-0.5 ${u > 0 ? 'text-green-600 font-bold' : u < 0 ? 'text-red-600 font-bold' : 'text-gray-300'}`}>{u !== 0 ? (u > 0 ? '+'+u : u) : '-'}</td>;
                                  })}
                                </tr>
                                <tr className="border-t border-gray-200 bg-gray-50">
                                  <td className="py-1 px-1 font-bold">Total</td>
                                  {vHoles.map(([hole, r]) => (
                                    <td key={hole} className={`text-center py-1 px-0.5 font-bold ${r.hole_points > 0 ? 'text-green-600' : r.hole_points < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                      {r.hole_points > 0 ? '+' : ''}{r.hole_points}
                                    </td>
                                  ))}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
}
